import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axiosClient from '@/services/axiosClient';
import AssociatedTasksView, {
  type AssociatedTaskViewItem,
} from '@/components/collaboration/AssociatedTasksView';
import type { Task, TaskPriority, TaskStatus } from './TasksPage';

interface AssociatedTasksProps {
  entityId: string;
  entityType: 'lead' | 'ticket' | 'prospect';
  entityName: string;
}

interface AssignableUser {
  userId?: string;
  displayName: string;
  email: string;
}

export function AssociatedTasks({
  entityId,
  entityType,
  entityName,
}: AssociatedTasksProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newPriority, setNewPriority] = useState<TaskPriority>('normal');
  const [newDueDate, setNewDueDate] = useState(
    () => new Date().toLocaleDateString('en-CA'),
  );
  const [newAssignee, setNewAssignee] = useState('');

  const loadTasks = useCallback(() => {
    setIsLoading(true);
    setLoadError('');
    axiosClient.get<Task[] | { items: Task[] }>('/tasks', {
      params: { associatedId: entityId, associatedType: entityType },
    })
      .then((response) => setTasks(
        Array.isArray(response.data) ? response.data : response.data.items,
      ))
      .catch(() => setLoadError('Tasks could not be loaded.'))
      .finally(() => setIsLoading(false));
  }, [entityId, entityType]);

  useEffect(loadTasks, [loadTasks]);

  useEffect(() => {
    axiosClient.get<AssignableUser[]>('/tasks/assignable-users')
      .then((response) => setAssignableUsers(response.data))
      .catch(() => setAssignableUsers([]));
  }, []);

  const advanceStatus = async (taskId: string, currentStatus: TaskStatus) => {
    let nextStatus: TaskStatus = currentStatus;
    if (currentStatus === 'assigned') nextStatus = 'in_progress';
    else if (currentStatus === 'in_progress') nextStatus = 'completed';
    else if (currentStatus === 'completed') nextStatus = 'assigned';

    try {
      await axiosClient.put(`/tasks/${taskId}/status`, { status: nextStatus });
      setTasks((current) => current.map((task) =>
        (task.taskId ?? task.id) === taskId ? { ...task, status: nextStatus } : task));
    } catch {
      // The existing entity wrapper remains non-optimistic on failure.
    }
  };

  const handleAddTask = async (event: FormEvent) => {
    event.preventDefault();
    if (!newSubject.trim()) return;

    const selectedUser = assignableUsers.find(
      (user) => (user.userId ?? user.email) === newAssignee,
    );
    const payload = {
      subject: newSubject,
      associatedId: entityId,
      associatedName: entityName,
      associatedType: entityType,
      priority: newPriority,
      status: 'assigned',
      dueDate: newDueDate || null,
      ...(selectedUser?.userId
        ? { assignedToUserId: selectedUser.userId }
        : { assignedToEmail: selectedUser?.email ?? null }),
    };

    try {
      const response = await axiosClient.post('/tasks', payload);
      const result = response.data as Task | { data?: Task; items?: Task[] };
      const taskToAdd = 'data' in result && result.data
        ? result.data
        : 'items' in result && Array.isArray(result.items)
          ? result.items[0]
          : result as Task;
      setTasks((current) => [taskToAdd, ...current]);
      setNewSubject('');
      setNewAssignee('');
      setIsModalOpen(false);
    } catch {
      // Existing behavior keeps the modal values available for another attempt.
    }
  };

  const viewTasks: AssociatedTaskViewItem[] = tasks.map((task) => ({
    id: task.taskId ?? task.id ?? `${task.subject}-${task.dueDate}`,
    subject: task.subject,
    priority: task.priority,
    status: task.status,
    dueDate: task.dueDate,
    assigneeLabel: task.assignee?.displayName
      ?? task.assignedToEmail
      ?? task.AssignedToEmail,
    assigneeActive: task.assignee?.isActive ?? undefined,
  }));

  return (
    <AssociatedTasksView
      title={`Tasks for ${entityName}`}
      tasks={viewTasks}
      isLoading={isLoading}
      loadError={loadError || undefined}
      onRetry={loadTasks}
      onCreate={() => setIsModalOpen(true)}
      createLabel="Add Custom Task"
      renderTaskActions={(task) => (
        <button
          type="button"
          onClick={() => void advanceStatus(task.id, task.status)}
          className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium capitalize transition-all ${
            task.status === 'assigned'
              ? 'border-blue-200 bg-blue-50 text-blue-700'
              : task.status === 'in_progress'
                ? 'border-amber-200 bg-amber-50 text-amber-700'
                : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {task.status.replace('_', ' ')}
          <ChevronRightIcon className="h-3 w-3 opacity-50" aria-hidden="true" />
        </button>
      )}
      createDialog={isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="relative w-full max-w-md rounded-xl border border-gray-100 bg-white p-5 shadow-xl">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              aria-label="Close task form"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <h3 className="mb-4 text-lg font-bold text-gray-900">Create New Task</h3>
            <form onSubmit={handleAddTask} className="space-y-4">
              <label className="block text-xs font-semibold uppercase text-gray-500">
                Task Subject
                <input
                  type="text"
                  value={newSubject}
                  onChange={(event) => setNewSubject(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm"
                  required
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-semibold uppercase text-gray-500">
                  Priority
                  <select
                    value={newPriority}
                    onChange={(event) => setNewPriority(event.target.value as TaskPriority)}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </label>
                <label className="block text-xs font-semibold uppercase text-gray-500">
                  Due Date
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={(event) => setNewDueDate(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm"
                  />
                </label>
              </div>
              <label className="block text-xs font-semibold uppercase text-gray-500">
                Assignee
                <select
                  value={newAssignee}
                  onChange={(event) => setNewAssignee(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm"
                >
                  <option value="">Unassigned</option>
                  {assignableUsers.map((user) => (
                    <option key={user.userId ?? user.email} value={user.userId ?? user.email}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white"
                >
                  Save Task
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    />
  );
}
